import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  AudiencePreviewResult,
  Campaign,
  CreateCampaignPayload,
  CreateSegmentPayload,
  CsvUploadResult,
  Segment,
  SegmentFilters,
  SegmentSource,
  SegmentSourceParams,
  SegmentPreviewResult,
  StartCampaignPayload,
  StartCampaignResult,
  UpdateCampaignPayload,
  UpdateSegmentPayload,
} from '@/client/types/campaigns';
import { lifecycleKeys } from './lifecycle.query';

const CAMPAIGNS = '/api/admin/v2/campaigns';
const SEGMENTS = '/api/admin/v2/segments';
const TIMEOUT = { timeout: 60_000 };

const unwrap = async <T>(promise: Promise<APIResponse<T>>): Promise<T> => {
  const res = await promise;
  return res.data as T;
};

const invalidateCampaigns = (qc: ReturnType<typeof useQueryClient>, cid?: string) => {
  qc.invalidateQueries({ queryKey: lifecycleKeys.campaigns() });
  if (cid) qc.invalidateQueries({ queryKey: lifecycleKeys.campaignDetail(cid) });
};

const invalidateSegments = (qc: ReturnType<typeof useQueryClient>, sid?: string) => {
  qc.invalidateQueries({ queryKey: lifecycleKeys.segments() });
  if (sid) qc.invalidateQueries({ queryKey: lifecycleKeys.segmentDetail(sid) });
};

const asCampaign = (raw: unknown): Campaign => {
  if (raw && typeof raw === 'object' && 'campaign' in raw) {
    const wrapped = raw as { campaign?: Campaign };
    if (wrapped.campaign) return wrapped.campaign;
  }
  return raw as Campaign;
};

const asSegment = (raw: unknown): Segment => {
  if (raw && typeof raw === 'object' && 'segment' in raw) {
    const wrapped = raw as { segment?: Segment };
    if (wrapped.segment) return wrapped.segment;
  }
  return raw as Segment;
};

// ─── Campaigns ────────────────────────────────────────────────────────────────

export const useCreateCampaignMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: CreateCampaignPayload; idempotencyKey?: string }) => {
      const raw = await unwrap(
        request<APIResponse<{ success?: boolean; campaign?: Campaign }>>(
          CAMPAIGNS,
          'POST',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
      return asCampaign(raw);
    },
    'Failed to create campaign',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateCampaigns(qc),
  });
};

export const useUpdateCampaignMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      cid: string;
      body: UpdateCampaignPayload;
      idempotencyKey?: string;
    }) => {
      const raw = await unwrap(
        request<APIResponse<{ success?: boolean; campaign?: Campaign }>>(
          `${CAMPAIGNS}/${args.cid}`,
          'PATCH',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
      return asCampaign(raw);
    },
    'Failed to update campaign',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { cid }) => invalidateCampaigns(qc, cid),
  });
};

export const useDeleteCampaignMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { cid: string; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean }>>(
          `${CAMPAIGNS}/${args.cid}`,
          'DELETE',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to delete campaign',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { cid }) => invalidateCampaigns(qc, cid),
  });
};

export const useStartCampaignMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      cid: string;
      body?: StartCampaignPayload;
      idempotencyKey?: string;
    }) => {
      return unwrap(
        request<APIResponse<StartCampaignResult>>(
          `${CAMPAIGNS}/${args.cid}/start`,
          'POST',
          args.body ?? {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to start campaign',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { cid }) => invalidateCampaigns(qc, cid),
  });
};

export const usePauseCampaignMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { cid: string; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean; status?: string }>>(
          `${CAMPAIGNS}/${args.cid}/pause`,
          'POST',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to pause campaign',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { cid }) => invalidateCampaigns(qc, cid),
  });
};

export const useResumeCampaignMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { cid: string; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean; status?: string }>>(
          `${CAMPAIGNS}/${args.cid}/resume`,
          'POST',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to resume campaign',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { cid }) => invalidateCampaigns(qc, cid),
  });
};

export const useCancelCampaignMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { cid: string; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean; status?: string }>>(
          `${CAMPAIGNS}/${args.cid}/cancel`,
          'POST',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to cancel campaign',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { cid }) => invalidateCampaigns(qc, cid),
  });
};

export const usePreviewCampaignAudienceMutation = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (args: { cid: string; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<AudiencePreviewResult>>(
          `${CAMPAIGNS}/${args.cid}/audience/preview`,
          'POST',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to preview audience',
  );
  return useMutation({ mutationFn: fn });
};

// ─── Segments ─────────────────────────────────────────────────────────────────

export const useCreateSegmentMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: CreateSegmentPayload; idempotencyKey?: string }) => {
      const raw = await unwrap(
        request<APIResponse<{ success?: boolean; segment?: Segment }>>(
          SEGMENTS,
          'POST',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
      return asSegment(raw);
    },
    'Failed to create segment',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateSegments(qc),
  });
};

export const useUpdateSegmentMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      sid: string;
      body: UpdateSegmentPayload;
      idempotencyKey?: string;
    }) => {
      const raw = await unwrap(
        request<APIResponse<{ success?: boolean; segment?: Segment }>>(
          `${SEGMENTS}/${args.sid}`,
          'PATCH',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
      return asSegment(raw);
    },
    'Failed to update segment',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { sid }) => invalidateSegments(qc, sid),
  });
};

export const useDeleteSegmentMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { sid: string; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean }>>(
          `${SEGMENTS}/${args.sid}`,
          'DELETE',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to delete segment',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { sid }) => invalidateSegments(qc, sid),
  });
};

export const usePreviewSegmentMutation = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (args: {
      filters: SegmentFilters;
      source?: SegmentSource;
      source_params?: SegmentSourceParams;
      idempotencyKey?: string;
    }) => {
      return unwrap(
        request<APIResponse<SegmentPreviewResult>>(
          `${SEGMENTS}/preview`,
          'POST',
          args.source && args.source !== 'users'
            ? { source: args.source, source_params: args.source_params ?? {} }
            : { filters: args.filters },
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to preview segment',
  );
  return useMutation({ mutationFn: fn });
};

export const useUploadSegmentCsvMutation = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      request<APIResponse<CsvUploadResult>>(`${SEGMENTS}/upload-csv`, 'POST', form, {
        ...TIMEOUT,
        idempotencyKey: generateIdempotencyKey(),
      }),
    );
  }, 'Failed to upload CSV');
  return useMutation({ mutationFn: fn });
};
